import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, interval, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntil as rxTakeUntil } from 'rxjs/operators';
import { AuthService } from '../../Services/auth.service';
import { Global } from '../../Shared/global';
import { ForgotPasswordRequest, ForgotPasswordResponse, ChangePasswordRequest } from '../../Models/Common/ForgotPasswordModels';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppContext } from '../../Shared/common';
import { AppConfigService } from '../../Services/appconfig.service';
import { ApplicationConfiguration } from '../../Models/ApplicationConfiguration';

@Component({
  selector: 'forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.css']
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  forgotPasswordForm: FormGroup;
  isLoading = false;
  isGeneratingCode = false;
  isSubmitting = false;
  isVerificationCodeExpired = false;
  errorMessage = '';
  successMessage = '';
  showPassword = false;
  showConfirmPassword = false;

  // Timer properties
  timerSeconds = 0;
  timerMinutes = 0;
  timerDisplay = '';
  private timerInterval: any;
  private readonly VERIFICATION_CODE_TIMEOUT_SECONDS = 300; // 5 minutes

  // Verification code response data
  private verificationCodeResponse: ForgotPasswordResponse | null = null;

  private destroy$ = new Subject<void>();
  private usernameCheckSubject$ = new Subject<string>();
  isCheckingUsername = false;
  usernameNotFound = false;
  userFound = false;

  // Theme properties
  loginLeftBoxHtml: SafeHtml = '';
  loginLeftCssClass: string = 'hero';
  loginFooterHtml: SafeHtml = '';
  appTitle: string = 'FitSpeex';
  appDescription: string = '';
  logoUrl: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private appctx: AppContext,
    private appConfigService: AppConfigService,
    private sanitizer: DomSanitizer
  ) {
    this.forgotPasswordForm = this.createForm();
  }

  ngOnInit(): void {
    // Redirect if already logged in
    if (this.authService.isLoggedIn()) {
      this.router.navigate([Global.DefaultPage]);
    }

    // Load and apply theme configuration
    this.loadThemeConfiguration();

    // Subscribe to theme changes
    this.appConfigService.appConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe(appConfig => {
        this.applyThemeToComponent(appConfig.CustomSettings);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopTimer();
  }

  /**
   * Create reactive form with validators
   */
  private createForm(): FormGroup {
    return this.formBuilder.group({
      userName: ['', [Validators.required, Validators.minLength(3)]],
      verificationCode: ['', [Validators.required, Validators.minLength(4)]],
      newPassword: [
        '',
        [Validators.required, Validators.minLength(6), this.passwordValidator]
      ],
      confirmPassword: [
        '',
        [Validators.required, Validators.minLength(6)]
      ]
    }, { validators: this.passwordMatchValidator });
  }

  /**
   * Password validator: require at least one uppercase, one digit
   */
  private passwordValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const password = control.value;
    const hasUppercase = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);

    if (!hasUppercase || !hasDigit) {
      return {
        passwordStrength: {
          requiresUppercase: !hasUppercase,
          requiresDigit: !hasDigit
        }
      };
    }

    return null;
  }

  /**
   * Password match validator: ensure new password matches confirm password
   */
  private passwordMatchValidator(formGroup: AbstractControl): ValidationErrors | null {
    const newPassword = formGroup.get('newPassword');
    const confirmPassword = formGroup.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    if (newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      // Clear password mismatch error if passwords now match
      if (confirmPassword.errors?.['passwordMismatch']) {
        confirmPassword.setErrors(null);
      }
    }

    return null;
  }

  /**
   * Request verification code for password reset
   */
  onGenerateVerificationCode(): void {
    const userNameControl = this.forgotPasswordForm.get('userName');

    if (!userNameControl || !userNameControl.value) {
      this.errorMessage = 'Please enter your username';
      return;
    }

    if (userNameControl.invalid) {
      userNameControl.markAsTouched();
      this.errorMessage = 'Username is invalid';
      return;
    }

    this.isGeneratingCode = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authService
      .requestForgotPasswordCode(userNameControl.value)
      .pipe(rxTakeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          debugger
          this.isGeneratingCode = false;
          this.verificationCodeResponse = response;
          this.isVerificationCodeExpired = false;

          this.successMessage = 'Verification code sent. Check your email.';
          Global.Log(`Verification code requested for user: ${userNameControl.value}`);

          // Start countdown timer
          this.startTimer();

          // Clear error and focus on verification code field
          this.errorMessage = '';
          setTimeout(() => {
            const verificationCodeInput = document.getElementById('verificationCode') as HTMLInputElement;
            if (verificationCodeInput) {
              verificationCodeInput.focus();
            }
          }, 500);
        },
        error: (error) => {
          debugger
          this.isGeneratingCode = false;
          this.verificationCodeResponse = null;

          if (Array.isArray(error.errors)) {
            this.errorMessage = error.errors.join('\n');
          } else if (error.message) {
            this.errorMessage = error.message;
          } else if (typeof error === 'string') {
            this.errorMessage = error;
          } else {
            this.errorMessage = 'Failed to request verification code. Please try again.';
          }

          Global.LogError('Verification code request failed:', error);
          this.stopTimer();
        }
      });
  }

  /**
   * Start countdown timer for verification code
   */
  private startTimer(): void {
    this.stopTimer();
    this.timerSeconds = this.VERIFICATION_CODE_TIMEOUT_SECONDS;
    this.updateTimerDisplay();

    this.timerInterval = setInterval(() => {
      this.timerSeconds--;

      if (this.timerSeconds <= 0) {
        this.stopTimer();
        this.isVerificationCodeExpired = true;
        this.successMessage = '';
        this.errorMessage = 'Verification code has expired. Please request a new one.';
        //this.forgotPasswordForm.get('verificationCode')?.disable();
        //this.forgotPasswordForm.get('newPassword')?.disable();
        //this.forgotPasswordForm.get('confirmPassword')?.disable();
      }

      this.updateTimerDisplay();
    }, 1000);
  }

  /**
   * Stop countdown timer
   */
  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  /**
   * Update timer display in MM:SS format
   */
  private updateTimerDisplay(): void {
    this.timerMinutes = Math.floor(this.timerSeconds / 60);
    const seconds = this.timerSeconds % 60;
    this.timerDisplay = `${this.timerMinutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Toggle new password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Toggle confirm password visibility
   */
  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  /**
   * Handle form submission - reset password
   */
  onSubmit(): void {
    if (this.forgotPasswordForm.invalid) {
      this.markFormGroupTouched(this.forgotPasswordForm);
      return;
    }

    if (this.isVerificationCodeExpired) {
      this.errorMessage = 'Verification code has expired. Please request a new one.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formValue = this.forgotPasswordForm.getRawValue();
    const changePasswordRequest: ChangePasswordRequest = {
      UserName: formValue.userName,
      ValidationCode: formValue.verificationCode,
      Password: formValue.newPassword,
      ConfirmPassword: formValue.confirmPassword,
      RequestId: this.verificationCodeResponse != null ? this.verificationCodeResponse.RequestId : 0,
      ExpiresAt: this.verificationCodeResponse != null ? this.verificationCodeResponse.ExpiresAt : new Date() 
    };

    this.authService
      .changePassword(changePasswordRequest)
      .pipe(rxTakeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.successMessage = 'Password reset successfully!';
          Global.Log('Password reset successful');

          this.stopTimer();
          this.forgotPasswordForm.reset();

          // Redirect to login after 2 seconds
          //setTimeout(() => {
          //  this.router.navigate(['/login']);
          //}, 2000);
        },
        error: (error) => {
          this.isSubmitting = false;

          if (Array.isArray(error.errors)) {
            this.errorMessage = error.errors.join('\n');
          } else if (error.message) {
            this.errorMessage = error.message;
          } else if (typeof error === 'string') {
            this.errorMessage = error;
          } else {
            this.errorMessage = 'Password reset failed. Please try again.';
          }

          Global.LogError('Password reset failed:', error);
        }
      });
  }

  /**
   * Navigate back to login
   */
  onCancel(): void {
    this.stopTimer();
    this.router.navigate(['/login']);
  }

  /**
   * Mark all form controls as touched to show validation errors
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Get form control getters
   */
  get userName() {
    return this.forgotPasswordForm.get('userName');
  }

  get verificationCode() {
    return this.forgotPasswordForm.get('verificationCode');
  }

  get newPassword() {
    return this.forgotPasswordForm.get('newPassword');
  }

  get confirmPassword() {
    return this.forgotPasswordForm.get('confirmPassword');
  }

  /**
   * Triggered when username input loses focus
   */
  onUsernameBlur(): void {
    const username = this.forgotPasswordForm.get('userName')?.value;
    if (username && username.length >= 3) {
      this.checkUsername(username);
    } else {
      this.resetUserLookup();
    }
  }

  private resetUserLookup(): void {
    this.isCheckingUsername = false;
    this.usernameNotFound = false;
    this.userFound = false;
  }

  private checkUsername(username: string): void {
    this.isCheckingUsername = true;
    this.errorMessage = '';
    this.usernameNotFound = false;
    this.userFound = false;

    this.authService.getUserByUserName(username).pipe(rxTakeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        if (response && response.UserName && response.IsRegistered) {
          this.userFound = true;
          this.usernameNotFound = false;
        } else {
          this.userFound = false;
          this.usernameNotFound = true;
        }
        this.isCheckingUsername = false;
      },
      error: () => {
        this.isCheckingUsername = false;
        this.userFound = false;
        this.usernameNotFound = true;
      }
    });
  }

  /**
   * Username validation setup (debounced lookup)
   */
  private setupUsernameValidation(): void {
    // Subscribe to value changes on the userName control
    const ctrl = this.forgotPasswordForm.get('userName');
    if (!ctrl) return;

    ctrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), rxTakeUntil(this.destroy$))
      .subscribe((username: string) => {
        if (username && username.length >= 3) {
          this.checkUsername(username);
        } else {
          this.resetUserLookup();
        }
      });
  }

  /**
   * Check if verification code is available
   */
  isVerificationCodeAvailable(): boolean {
    return this.verificationCodeResponse !== null && !this.isVerificationCodeExpired;
  }

  /**
   * Check if specific validation error exists
   */
  hasError(controlName: string, errorName: string): boolean {
    const control = this.forgotPasswordForm.get(controlName);
    return !!(control && control.hasError(errorName) && control.touched);
  }

  /**
   * Get error message for control
   */
  getErrorMessage(controlName: string): string {
    const control = this.forgotPasswordForm.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.hasError('required')) {
      return `${this.getFieldLabel(controlName)} is required`;
    }

    if (control.hasError('minlength')) {
      const minLength = control.getError('minlength').requiredLength;
      return `${this.getFieldLabel(controlName)} must be at least ${minLength} characters`;
    }

    if (control.hasError('passwordStrength')) {
      const errors = control.getError('passwordStrength');
      let message = 'Password must contain ';
      if (errors.requiresUppercase) {
        message += 'at least one uppercase letter';
      }
      if (errors.requiresDigit) {
        message += errors.requiresUppercase ? ' and one digit' : 'at least one digit';
      }
      return message;
    }

    if (control.hasError('passwordMismatch')) {
      return 'Passwords do not match';
    }

    return 'Invalid input';
  }

  /**
   * Get friendly field label for error messages
   */
  private getFieldLabel(controlName: string): string {
    const labels: { [key: string]: string } = {
      userName: 'Username',
      verificationCode: 'Verification Code',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password'
    };
    return labels[controlName] || controlName;
  }

  /**
   * Load theme configuration
   */
  private loadThemeConfiguration(): void {
    this.appConfigService.loadAppConfig().then(config => {
      this.applyThemeToComponent(config);

      if (!this.loginLeftBoxHtml || this.loginLeftCssClass === '') {
        this.setDefaultTheme();
      }
    }).catch(error => {
      Global.LogError('[LoginComponent] Error loading theme:', error);
      // Use defaults if theme loading fails
      this.setDefaultTheme();
    });
  }

  /**
   * Apply theme to component
   */
  private applyThemeToComponent(config: ApplicationConfiguration): void {
    if (!config || !config?.CustomSettings) {
      Global.Log('[LoginComponent] No custom settings found in config');
      this.setDefaultTheme();
      return;
    }
    if (config.CustomSettings.loginLeftBoxHtml) {
      this.loginLeftBoxHtml = this.sanitizer.bypassSecurityTrustHtml(config.CustomSettings.loginLeftBoxHtml);
    }

    if (config.CustomSettings.loginLeftCssClass) {
      this.loginLeftCssClass = config.CustomSettings.loginLeftCssClass;
    }

    if (config.CustomSettings.loginFooterHtml) {
      this.loginFooterHtml = this.sanitizer.bypassSecurityTrustHtml(config.CustomSettings.loginFooterHtml);
    }

    if (config.CustomSettings.appTitle) {
      this.appTitle = config.CustomSettings.appTitle;
    }

    if (config.CustomSettings.appDescription) {
      this.appDescription = config.CustomSettings.appDescription;
    }

    if (config.CustomSettings.logoUrl) {
      this.logoUrl = config.CustomSettings.logoUrl;
    }

    Global.Log('[LoginComponent] Theme applied');
  }

  /**
   * Set default theme if loading fails
   */
  private setDefaultTheme(): void {
    this.appTitle = 'FitSpeex';
    this.loginLeftCssClass = 'hero';
    this.loginLeftBoxHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<div class="hero-overlay"></div> <div class="logo"> <div class="logo-icon">✦</div> <span>Fitspeex</span> </div> <div class="hero-content"> <h1> Move <span>Your Way</span> </h1> <p> One platform for every kind of fitness. Gym, Yoga, Calisthenics and more. </p> </div> <!-- FITNESS MENU --> <div class="fitness-menu"> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">fitness_center</mat-icon> </div> <span>Gym</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">self_improvement</mat-icon> </div> <span>Yoga</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">sports_gymnastics</mat-icon> </div> <span>Calisthenics</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">directions_run</mat-icon> </div> <span>Cardio</span> </div> <div class="menu-item"> <div class="menu-icon material-icons-round"> <mat-icon fontSet="material-symbols-rounded">more_horiz</mat-icon> </div> <span>More</span> </div> </div>`
    );
    this.loginFooterHtml = this.sanitizer.bypassSecurityTrustHtml(
      `<div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">grid_view</mat-icon> </div> <h3>All-in-One Fitness</h3> <p> Access gym, yoga, calisthenics, cardio and more in one place. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">person</mat-icon> </div> <h3>Personalized for You</h3> <p> Your goals, your plan. We adapt to you. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">analytics</mat-icon> </div> <h3>Track & Progress</h3> <p> Monitor your progress and stay motivated every day. </p> </div> <div class="feature-card"> <div class="feature-icon material-symbols-rounded"> <mat-icon fontSet="material-symbols-rounded">verified_user</mat-icon> </div> <h3>Trusted & Secure</h3> <p> Your data is safe with us. Focus on what matters. </p> </div>`
    );
  }
}
